import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Pressable, StyleSheet, Text, useColorScheme, View } from "react-native";

import { captureException } from "@/lib/telemetry";

/**
 * What a screen shows instead of dying.
 *
 * Before this, the application had exactly **one** `ErrorBoundary` — on Profile
 * — and the root layout exported none, so a render failure anywhere else was a
 * blank screen with no way out but force-quitting. That is `MOBILE_REVIEW`
 * M-01 item 1 and `WEB_REVIEW` WEB-03, which are the same task on the same
 * source tree.
 *
 * ── Why this deliberately uses no app context ────────────────────────────────
 *
 * A root boundary catches failures in the provider tree itself. If it reached
 * for `useAppTheme()` and the theme provider was what threw, the fallback would
 * throw while rendering the fallback — React unmounts the whole tree and the
 * result is worse than the bug being handled. So: `useColorScheme` from React
 * Native (no context), a local palette, and no dependency on anything mounted
 * above it.
 *
 * `useTranslation` is the one exception, and it is safe for a different reason:
 * with no provider it falls back to the default i18n instance, and if i18n
 * never initialised it returns the key rather than throwing. Every string below
 * passes an English `defaultValue` so that case reads as English rather than as
 * `common.tryAgain`.
 *
 * The three keys used already exist in **all 17 locales**, so this ships
 * translated rather than joining the hardcoded-English backlog.
 */

type ErrorFallbackProps = {
  /** The thrown error, from Expo Router's `ErrorBoundaryProps`. */
  error: Error;
  /** Re-renders the subtree. Expo Router supplies this. */
  retry: () => void;
  /**
   * Which boundary caught it — "root", "chat", "practice", "billing".
   *
   * Reported as context so a crash can be attributed to a screen without
   * attaching the screen's data, which is mostly denylisted.
   */
  scope: string;
};

export function ErrorFallback({ error, retry, scope }: ErrorFallbackProps) {
  const { t } = useTranslation();
  const isDark = useColorScheme() === "dark";
  const colors = getFallbackColors(isDark);
  const styles = createStyles(colors);

  useEffect(() => {
    // A no-op until a telemetry client is installed, and redacted when one is.
    // The boundary is the only place that knows a render failed at all — React
    // does not surface it anywhere else once the fallback is showing.
    captureException(error, { scope, boundary: true }, "fatal");
  }, [error, scope]);

  return (
    <View style={styles.container}>
      <Text accessibilityRole="header" style={styles.title}>
        {t("common.somethingWentWrong", { defaultValue: "Something went wrong." })}
      </Text>

      <Text style={styles.body}>
        {t("errors.tryAgainInAMoment", {
          defaultValue: "Please try again in a moment.",
        })}
      </Text>

      {/*
        The message can contain user content — a validation error quoting what
        was typed, a parse failure quoting a payload. It is shown in development
        only, for the same reason the Profile boundary does it.
      */}
      {__DEV__ && error?.message ? (
        <Text numberOfLines={4} style={styles.details}>
          {error.message}
        </Text>
      ) : null}

      <Pressable
        accessibilityRole="button"
        accessibilityLabel={t("common.tryAgain", { defaultValue: "Try again" })}
        onPress={retry}
        style={({ pressed }) => [styles.button, pressed && styles.buttonPressed]}
      >
        <Text style={styles.buttonLabel}>
          {t("common.tryAgain", { defaultValue: "Try again" })}
        </Text>
      </Pressable>
    </View>
  );
}

/**
 * A local palette, matching the one Profile's boundary defines for itself.
 *
 * Duplicated rather than imported from the theme module on purpose — see the
 * note above about not depending on anything that may be what failed.
 */
function getFallbackColors(isDark: boolean) {
  return {
    screen: isDark ? "#0D1724" : "#FCFDFF",
    textPrimary: isDark ? "#EFF5FF" : "#102D51",
    textBody: isDark ? "#C0CDDD" : "#425873",
    textMuted: isDark ? "#8999AD" : "#929DAC",
    primary: isDark ? "#6EA8FF" : "#2468D8",
    white: "#FFFFFF",
  };
}

function createStyles(colors: ReturnType<typeof getFallbackColors>) {
  return StyleSheet.create({
    container: {
      flex: 1,
      alignItems: "center",
      justifyContent: "center",
      gap: 12,
      padding: 24,
      backgroundColor: colors.screen,
    },
    title: {
      fontSize: 18,
      fontWeight: "600",
      textAlign: "center",
      color: colors.textPrimary,
    },
    body: {
      fontSize: 15,
      lineHeight: 21,
      textAlign: "center",
      color: colors.textBody,
    },
    details: {
      fontSize: 12,
      textAlign: "center",
      color: colors.textMuted,
    },
    button: {
      marginTop: 8,
      paddingVertical: 12,
      paddingHorizontal: 24,
      borderRadius: 999,
      backgroundColor: colors.primary,
    },
    buttonPressed: { opacity: 0.85 },
    buttonLabel: {
      fontSize: 15,
      fontWeight: "600",
      color: colors.white,
    },
  });
}
